import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getSpeakers,
  getFeaturedSpeakers,
  getFavoriteSpeakerIds,
  toggleFavoriteSpeaker,
  getOrganizations as fetchOrganizations,
} from '../lib/speakerService';
import SpeakerCard from '../components/speakers/SpeakerCard';
import FeaturedSpeakerCard from '../components/speakers/FeaturedSpeakerCard';
import SpeakerFilters from '../components/speakers/SpeakerFilters';
import { SpeakerCardSkeletonGrid } from '../components/speakers/SpeakerCardSkeleton';
import SpeakerEmptyState from '../components/speakers/SpeakerEmptyState';

const SpeakersPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [speakers, setSpeakers] = useState([]);
  const [featuredSpeakers, setFeaturedSpeakers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [organization, setOrganization] = useState(searchParams.get('org') || 'all');
  const [topic, setTopic] = useState(searchParams.get('topic') || '');

  useEffect(() => {
    if (!user) { setFavoriteIds([]); return; }
    getFavoriteSpeakerIds(user.id).then(setFavoriteIds);
  }, [user]);

  useEffect(() => {
    fetchOrganizations().then(setOrganizations);
  }, []);

  useEffect(() => {
    const loadSpeakers = async () => {
      setLoading(true);
      const { data } = await getSpeakers({
        organization: organization !== 'all' ? organization : undefined,
        search: searchTerm || undefined,
      });
      setSpeakers(data);
      setLoading(false);
    };
    loadSpeakers();
  }, [searchTerm, organization]);

  useEffect(() => {
    if (searchTerm || organization !== 'all' || topic) {
      setFeaturedSpeakers([]);
      return;
    }
    getFeaturedSpeakers().then(({ data }) => setFeaturedSpeakers(data));
  }, [searchTerm, organization, topic]);

  useEffect(() => {
    const params = {};
    if (searchTerm) params.q = searchTerm;
    if (organization !== 'all') params.org = organization;
    if (topic) params.topic = topic;
    setSearchParams(params, { replace: true });
  }, [searchTerm, organization, topic, setSearchParams]);

  const allTopics = useMemo(() => {
    const topicSet = new Set();
    speakers.forEach((s) => {
      if (s.topics && Array.isArray(s.topics)) {
        s.topics.forEach((t) => topicSet.add(t));
      }
    });
    return [...topicSet].sort();
  }, [speakers]);

  const filteredSpeakers = useMemo(() => {
    if (!topic) return speakers;
    return speakers.filter((s) => s.topics && s.topics.includes(topic));
  }, [speakers, topic]);

  const handleToggleFavorite = useCallback(async (speakerId) => {
    if (!user) { window.location.href = '/login'; return; }
    const isFav = favoriteIds.includes(speakerId);
    setFavoriteIds((prev) => isFav ? prev.filter((id) => id !== speakerId) : [...prev, speakerId]);
    await toggleFavoriteSpeaker(user.id, speakerId, isFav);
  }, [user, favoriteIds]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setOrganization('all');
    setTopic('');
  };

  const handleClickTopic = (t) => setTopic(t);

  const hasActiveFilters = searchTerm || organization !== 'all' || topic;
  const showFeatured = featuredSpeakers.length > 0 && !hasActiveFilters;

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen pt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
          Conference Speakers
        </h1>
        <p className="text-gray-600 dark:text-gray-400 max-w-2xl">
          Discover ministers, pastors, teachers, evangelists, worship leaders, and ministry voices serving conferences throughout the Apostolic community.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SpeakerFilters
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          organization={organization}
          setOrganization={setOrganization}
          topic={topic}
          setTopic={setTopic}
          organizations={organizations}
          allTopics={allTopics}
          onClearFilters={handleClearFilters}
        />

        {showFeatured && (
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Featured Speakers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredSpeakers.map((speaker) => (
                <FeaturedSpeakerCard key={speaker.id} speaker={speaker} onClickTopic={handleClickTopic} />
              ))}
            </div>
          </section>
        )}

        <section className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {organization !== 'all' ? `${organization} Speakers` : 'All Speakers'}
              {topic && <span className="text-primary ml-2">· {topic}</span>}
            </h2>
            {!loading && (
              <span className="text-sm text-gray-500">
                {filteredSpeakers.length} {filteredSpeakers.length === 1 ? 'speaker' : 'speakers'}
              </span>
            )}
          </div>

          {loading ? (
            <SpeakerCardSkeletonGrid count={8} />
          ) : filteredSpeakers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredSpeakers.map((speaker) => (
                <SpeakerCard
                  key={speaker.id}
                  speaker={speaker}
                  isFavorite={favoriteIds.includes(speaker.id)}
                  onToggleFavorite={handleToggleFavorite}
                  onClickTopic={handleClickTopic}
                />
              ))}
            </div>
          ) : (
            <SpeakerEmptyState hasFilters={hasActiveFilters} onClearFilters={handleClearFilters} />
          )}
        </section>

        <section className="mb-16 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Interested in Speaking?</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
            Create your ACP speaker profile and make it easier for conference organizers and attendees to discover your ministry.
          </p>
          <Link
            to="/speakers/apply"
            className="inline-flex items-center px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors"
          >
            Create Speaker Profile
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </section>
      </div>
    </div>
  );
};

export default SpeakersPage;
